import config from "config/website";
import { useCallback, useRef } from "react";

const preloadConfig = {
  intersectionObserverOptions: { rootMargin: "0px" },
  timeoutBeforePreload: 500,
  ...(config && config.preload ? config.preload : {}),
};

const shouldUseIntersectionObservers =
  typeof navigator !== "undefined" &&
  // @ts-ignore experimental flag
  (!("connection" in navigator) || navigator.connection.saveData !== true);
let listeners = new Map();
let alreadyListened = new Set();
let observer = null;

const getObserver = () => {
  if (typeof window === "undefined" || !window.IntersectionObserver) {
    return;
  }

  if (!observer) {
    observer = new window.IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!listeners.has(entry.target)) {
          return;
        }

        const callback = listeners.get(entry.target);
        if (entry.isIntersecting || entry.intersectionRatio > 0) {
          callback.onEnter();
        } else {
          callback.onLeave();
        }
      });
    }, preloadConfig.intersectionObserverOptions);
  }

  return observer;
};

const listenIntersection = (element, callback) => {
  const observer = getObserver();
  if (!observer) {
    return () => {};
  }

  observer.observe(element);

  let removeListener = () => {
    try {
      observer.unobserve(element);
    } catch (e) {
      // if the listener was already removed it's ok not to be able to remove it from the Map
    }
    listeners.delete(element);
  };

  let timeout;
  listeners.set(element, {
    onEnter: () => {
      if (!timeout) {
        timeout = setTimeout(() => {
          removeListener();
          callback();
          timeout = null;
        }, preloadConfig.timeoutBeforePreload);
      }
    },
    onLeave: () => {
      if (timeout) {
        clearTimeout(timeout);
      }
      timeout = null;
    },
  });

  return removeListener;
};

/**
 * @typedef {Object} ListenerRef
 * @property {HTMLElement} element
 * @property {Function} removeListener
 */

const useIntersectionObserver = ({ callback, key }) => {
  /** @type {import("react").MutableRefObject<ListenerRef>} */
  const listenerRef = useRef();
  return useCallback(
    (element) => {
      if (!shouldUseIntersectionObservers) {
        return;
      }

      if (!element && listenerRef.current) {
        listenerRef.current.removeListener();
        listenerRef.current = null;
      }

      const hasNoPreviousElement = element && !listenerRef.current;
      const hasElementChanged =
        !listenerRef.current || listenerRef.current.element !== element;
      if (
        element &&
        hasNoPreviousElement &&
        hasElementChanged &&
        !alreadyListened.has(key)
      ) {
        listenerRef.current = {
          element: element,
          removeListener: listenIntersection(element, () => {
            alreadyListened.add(key);
            callback();
          }),
        };
      }
    },
    [callback, key]
  );
};

export default useIntersectionObserver;
